'use client';

import { Calendar, MapPin, CreditCard, Download, Mail, Ticket } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { mockEvents } from '@/lib/mock-data';
import { EmptyState } from '@/components/EmptyState';

// Mock user bookings data
interface Booking {
  id: string;
  eventId: string;
  packageTier: string;
  packageName: string;
  bookingDate: string;
  guests: number;
  totalPaid: number;
  bookingReference: string;
  status: 'upcoming' | 'completed' | 'cancelled';
}

const mockBookings: Booking[] = [
  {
    id: 'book-001',
    eventId: 'evt-001',
    packageTier: 'Platinum',
    packageName: 'Premium VIP Package',
    bookingDate: '2026-01-15',
    guests: 2,
    totalPaid: 3000,
    bookingReference: 'TTL-ABC123DEF',
    status: 'upcoming',
  },
  {
    id: 'book-002',
    eventId: 'evt-003',
    packageTier: 'Diamond',
    packageName: 'Eras Ultimate VIP',
    bookingDate: '2026-02-01',
    guests: 2,
    totalPaid: 7000,
    bookingReference: 'TTL-XYZ789GHI',
    status: 'upcoming',
  },
];

export default function BookingsPage() {
  const hasBookings = mockBookings.length > 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getStatusColor = (status: Booking['status']) => {
    switch (status) {
      case 'upcoming':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'completed':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="mb-4 font-serif text-5xl font-bold text-white">
            My{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              Bookings
            </span>
          </h1>
          <p className="text-lg text-gray-400">
            Manage your VIP concert experiences and upcoming events
          </p>
        </div>

        {/* Stats Summary */}
        {hasBookings && (
          <div className="mb-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-[#D4AF37]/20 p-3">
                  <Ticket className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Total Bookings</p>
                  <p className="font-serif text-3xl font-bold text-white">{mockBookings.length}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-green-500/20 p-3">
                  <Calendar className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Upcoming Events</p>
                  <p className="font-serif text-3xl font-bold text-white">
                    {mockBookings.filter((b) => b.status === 'upcoming').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-[#D4AF37]/20 p-3">
                  <CreditCard className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Total Spent</p>
                  <p className="font-serif text-3xl font-bold text-white">
                    {mockBookings.reduce((sum, b) => sum + b.totalPaid, 0).toLocaleString()} USDT
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings List */}
        {!hasBookings ? (
          <EmptyState
            title="No Bookings Yet"
            description="You haven't made any bookings yet. Explore our upcoming VIP events and secure your premium experience today!"
            actionLabel="Browse Events"
            actionHref="/events"
          />
        ) : (
          <div className="space-y-6">
            {mockBookings.map((booking) => {
              const event = mockEvents.find((e) => e.id === booking.eventId);
              if (!event) return null;

              const eventDate = new Date(event.date);
              const isUpcoming = eventDate > new Date();

              return (
                <div
                  key={booking.id}
                  className="group overflow-hidden rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/80 to-black/60 backdrop-blur-md transition-all duration-300 hover:border-[#D4AF37]/50 hover:shadow-[0_0_30px_rgba(212,175,55,0.2)]"
                >
                  <div className="grid gap-0 md:grid-cols-12">
                    {/* Event Image */}
                    <div className="relative aspect-video md:col-span-4 md:aspect-auto">
                      <Image
                        src={event.imageUrl}
                        alt={event.eventTitle}
                        fill
                        className="object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/80 md:bg-gradient-to-r"></div>

                      {/* Status Badge */}
                      <div className="absolute right-4 top-4">
                        <div
                          className={`rounded-full border px-3 py-1 text-xs font-bold uppercase backdrop-blur-md ${getStatusColor(booking.status)}`}
                        >
                          {booking.status}
                        </div>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="p-6 md:col-span-8">
                      <div className="mb-4 flex flex-col justify-between gap-4 md:flex-row md:items-start">
                        <div className="flex-1">
                          <h3 className="mb-2 font-serif text-2xl font-bold text-white">
                            {event.artistName}
                          </h3>
                          <p className="mb-4 text-gray-400">{event.eventTitle}</p>

                          <div className="mb-4 flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Calendar className="h-4 w-4 text-[#D4AF37]" />
                              <span>{formatDate(event.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <MapPin className="h-4 w-4 text-[#D4AF37]" />
                              <span>
                                {event.venue}, {event.city}
                              </span>
                            </div>
                          </div>

                          {/* Package Info */}
                          <div className="rounded-lg border border-[#D4AF37]/20 bg-black/40 p-4">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-sm text-gray-400">Package</span>
                              <span className="rounded-full bg-[#D4AF37]/20 px-3 py-1 text-xs font-bold text-[#D4AF37]">
                                {booking.packageTier}
                              </span>
                            </div>
                            <p className="mb-2 font-semibold text-white">{booking.packageName}</p>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-400">{booking.guests} guests</span>
                              <span className="font-bold text-white">
                                {booking.totalPaid.toLocaleString()} USDT
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Booking Reference */}
                        <div className="rounded-lg border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-4">
                          <p className="mb-1 text-xs text-gray-400">Booking Ref</p>
                          <p className="font-mono text-sm font-bold text-[#D4AF37]">
                            {booking.bookingReference}
                          </p>
                          <p className="mt-2 text-xs text-gray-400">
                            Booked: {formatDate(booking.bookingDate)}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-3">
                        {isUpcoming && (
                          <>
                            <button className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-5 py-2 text-sm font-semibold text-black transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]">
                              <Download className="h-4 w-4" />
                              Download Tickets
                            </button>

                            <button className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10">
                              <Mail className="h-4 w-4" />
                              Email Details
                            </button>

                            <Link
                              href={`/events/${event.id}`}
                              className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                            >
                              View Event
                            </Link>
                          </>
                        )}

                        {booking.status === 'completed' && (
                          <button className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10">
                            Leave Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Section */}
        {hasBookings && (
          <div className="mt-12 rounded-xl border border-[#D4AF37]/20 bg-gradient-to-r from-black/80 to-black/60 p-8 text-center backdrop-blur-md">
            <h3 className="mb-4 font-serif text-2xl font-bold text-white">
              Ready for Your Next VIP Experience?
            </h3>
            <p className="mb-6 text-gray-400">
              Explore more upcoming concerts and exclusive VIP packages
            </p>
            <Link
              href="/events"
              className="inline-block rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-8 py-3 font-bold text-black transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
            >
              Browse Events
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
